//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import PersonRoleModel from '../models/person-role.model';

/**
 * @service PersonRoleService
 * @description Service for managing Person Roles within the Trading Community module.
 * Person Roles define the functional responsibilities and permissions associated with a user in the system.
 * Unlike Person Categories (which classify the type of user like Farmer or Dealer), Roles often dictate
 * specific access levels or job functions (e.g., 'Manager', 'Technician', 'Admin').
 *
 * This service provides the interface to retrieve these role definitions from the backend API.
 * It is essential for:
 * 1. **Access Control**: Determining what actions a user can perform based on their assigned role.
 * 2. **User Management**: Assigning roles during user creation or updates.
 * 3. **UI Logic**: Conditionally displaying elements based on available roles.
 *
 * The service extends the generic `Service` class to inherit standard CRUD capabilities and HTTP handling,
 * specifically targeting the `/tradingcommunity/personrole/` endpoint.
 *
 * @feature 1. Retrieve all defined Person Roles from the system.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure comprehensive lists.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<PersonRoleModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/personrole/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PersonRoleService extends Service<PersonRoleModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/personrole/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/personrole/');
  }

  /**
   * @method getAllPersonRoles
   * @async
   * @description Fetch all person role records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all roles are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<PersonRoleModel>>} Promise resolving to an array of PersonRoleModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * personRoleService.getAllPersonRoles().then(roles => {
   *   console.log('Available Roles:', roles);
   * });
   */
  public getAllPersonRoles() {
    return new Promise<Array<PersonRoleModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PersonRoleModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
